import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

interface PromoDialogProps {
  titleId: string
  title: string
  subtitle?: ReactNode
  busy: boolean
  /** Typed-in values survive a stray click on the backdrop. */
  dirty: boolean
  onClose: () => void
  children: (close: () => void) => ReactNode
}

/** Native modal for the promo code forms: Escape and the backdrop close it unless a request is running. */
export function PromoDialog({ titleId, title, subtitle, busy, dirty, onClose, children }: PromoDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const pressedOnBackdrop = useRef(false)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const close = () => {
    if (!busy) dialogRef.current?.close()
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={(event) => busy && event.preventDefault()}
      onMouseDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (pressedOnBackdrop.current && event.target === event.currentTarget && !dirty) close()
      }}
      aria-labelledby={titleId}
      className="m-auto max-h-[calc(100dvh-24px)] w-[min(520px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <div className="flex flex-col gap-4 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="font-display text-[19px] leading-tight font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]"
            >
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-[13px] text-ink-muted [overflow-wrap:anywhere]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            aria-label="Закрыть"
            className="-mt-1.5 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50 md:size-9"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>
        {children(close)}
      </div>
    </dialog>
  )
}

/** Footer of a promo form: buttons share the width on a phone and stay 44px tall. */
export function DialogActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap justify-end gap-2 border-t border-line-soft pt-4 max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
      {children}
    </div>
  )
}

export function FieldHint({ children }: { children: ReactNode }) {
  return <p className="text-[12.5px] text-ink-muted text-balance-pretty">{children}</p>
}
